import { useRef, useState } from "react";
import { ApiError } from "../api/client";
import { getPassword } from "../api/users";

/**
 * §6.3 密码显示策略:默认掩码,点击按钮才发起单独请求获取明文,
 * 15 秒后自动恢复掩码。
 *
 * 此前版本请求失败时只 console.error,不给任何界面反馈,点击后表面上
 * "什么都没发生"(按钮回到"显示密码"文案,像是没点中),容易被误判成
 * "按钮失效"。现在把错误信息显示在按钮旁边(如 accountId 传空、后端
 * 404/403 等),方便判断失败原因。
 */
export function PasswordCell({ accountId, email, canReveal }: { accountId: string; email: string; canReveal: boolean }) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleReveal = async () => {
    if (!accountId) {
      setError("未选择目标账号");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await getPassword(accountId, email);
      setRevealed(resp.password);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setRevealed(null), 15000);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.message}(status=${err.status})`
          : err instanceof Error
            ? err.message
            : "获取密码失败";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (!canReveal) {
    return <span style={{ color: "#94a3b8" }}>••••••••</span>;
  }

  if (revealed) {
    return <span style={{ fontFamily: "monospace" }}>{revealed}</span>;
  }

  return (
    <span>
      <button onClick={handleReveal} disabled={loading}>
        {loading ? "加载中..." : "显示密码"}
      </button>
      {error && <div style={{ color: "#dc2626", fontSize: 11, marginTop: 4 }}>{error}</div>}
    </span>
  );
}
